import React, { useState, useRef, useEffect } from 'react'
import { Box, TextInput, Layer, Header, Text, Button, Image } from 'grommet'
import { FormNext } from 'grommet-icons'
import { I18n, GlobalConfig } from 'galarm-config'
import { Close } from 'grommet-icons'
import { useDispatch, useSelector } from 'react-redux'
import { AlarmUtils, Utils } from 'galarm-shared'
import { Constants } from 'galarm-config'
import ConfigureAlarmParticipants from './ConfigureAlarmParticipants'
import HoverBox from './HoverBox'
import LabelText from './LabelText'
import TintedImage from './TintedImage'
import RichTextEditor from './RichTextEditor'
import ParticipantsList from './ParticipantsList'
import { ActionCreators } from 'galarm-shared'
import SelectImage from './SelectImage'
import { NavigationUtils } from 'galarm-ps-api'
import objPick from 'lodash/pick'

const EditAlarmHeader = ({ onSave }) => {
  const dispatch = useDispatch()
  return (
    <Header
      className="header"
      background="brand"
      pad="small"
      direction="row"
      justify="between">
      <Text color="white" level={5}>
        {I18n.t('newInstantAlarm')}
      </Text>
      <Box direction="row" align="center">
        <Button
          plain
          color="white"
          label={I18n.t('sendAlarm')}
          onClick={onSave}
        />
        <Button
          icon={<Close />}
          onClick={() => dispatch(ActionCreators.hideNewInstantAlarmWizard())}
        />
      </Box>
    </Header>
  )
}

const EditInstantAlarm = props => {
  const colorScheme = useSelector(state => state.userSettings.webColorScheme)

  const [showNotes, setShowNotes] = useState(false)
  const [showAlarmPhotoSelector, setShowAlarmPhotoSelector] = useState(false)

  const displayAlarmPhotoSelector = event => {
    event.stopPropagation()
    setShowAlarmPhotoSelector(true)
  }

  const hideAlarmPhotoSelector = () => {
    setShowAlarmPhotoSelector(false)
  }

  const options = [I18n.t('uploadImage'), I18n.t('removeImage')]

  const defaultAdditionalConfigurationToShow = {
    participantsPicker: false
  }

  const scrollToRightRef = useRef(null)
  const inputFile = useRef(null)

  const onSelectFile = event => {
    var file = event.target.files[0]
    // Fix for the issue of adding the same file
    event.target.value = ''
    if (file.size > GlobalConfig.maxProfileImageSize) {
      NavigationUtils.showAlert(
        I18n.t('cantUploadImage'),
        I18n.t('selectSmallerImage')
      )
    } else {
      props.onUploadAlarmPhoto(file)
    }
  }

  const onNameChange = event => props.onNameChange(event.target.value)

  const [additionalConfigurationToShow, setAdditionalConfigurationToShow] =
    useState(defaultAdditionalConfigurationToShow)

  const hideAdditionalConfiguration = () =>
    setAdditionalConfigurationToShow(defaultAdditionalConfigurationToShow)

  const displayParticipantsPicker = () =>
    setAdditionalConfigurationToShow({
      ...defaultAdditionalConfigurationToShow,
      participantsPicker: true
    })

  // Run this on every update, so no dependencies are needed
  useEffect(() => {
    scrollToRightRef.current?.scrollIntoView({ behavior: 'smooth' })
  })

  const handleChangeNotes = () => {
    setShowNotes(true)
  }

  const onSelectUploadImage = () => {
    inputFile.current.click()
  }

  const onRemoveImage = () => {
    props.onDeleteAlarmPhoto()
  }

  const onShowAlarmPhoto = event => {
    event.stopPropagation()
    inputFile.current.click()
  }

  const onUnselectParticipant = contactId => {
    const newSelectedContacts = props.backupContacts.filter(
      c => c.id !== contactId
    )
    props.onSaveBackups(newSelectedContacts, null)
  }

  const updateParticipantsOrder = participants => {
    props.onSaveBackups(participants, null)
  }

  const onSelectParticipant = participant => {
    if (participant.participantType === Constants.ParticipantTypes.CONTACT) {
      if (props.backupGroup) {
        NavigationUtils.showAlert(
          '',
          I18n.t('previouslySelectedGroupIsUnselected')
        )
      }
      let newBackupContacts
      if (participant.selected) {
        newBackupContacts = props.backupContacts.filter(
          item => item.id !== participant.id
        )
      } else {
        newBackupContacts = props.backupContacts.concat([
          objPick(participant, ['id', 'name', 'mobileNumber'])
        ])
      }
      props.onSaveBackups(newBackupContacts, null)
    } else if (
      participant.participantType === Constants.ParticipantTypes.GROUP
    ) {
      if (props.backupContacts.length > 0) {
        NavigationUtils.showAlert(
          '',
          I18n.t('previouslySelectedContactsAreUnselected')
        )
      }
      if (props.backupGroup && props.backupGroup.id === participant.id) {
        props.onSaveBackups([], null)
      } else {
        props.onSaveBackups([], objPick(participant, ['id', 'name', 'members']))
      }
    }
  }

  const updateMembersConfig = members => {
    let newMembers = props.backupGroup.members.map(member => {
      const newMember = Utils.getObjectWithId(members, member.id)
      return {
        ...member,
        state: newMember.state
      }
    })

    let newBackupGroup = {
      ...props.backupGroup,
      members: newMembers
    }

    props.onSaveBackups([], newBackupGroup)
  }

  const onSave = () => {
    props.onSaveAlarm()
  }

  return (
    <Box flex background="textBackgroundColor">
      <EditAlarmHeader onSave={onSave} />
      <Box flex overflow={{ horizontal: 'auto' }}>
        <Box flex direction="row" width={{ min: '1200px' }}>
          <Box
            pad={{ vertical: 'small' }}
            border="right"
            fill="vertical"
            width={'400px'}
            overflow="auto">
            <Box flex>
              <HoverBox onClick={hideAdditionalConfiguration}>
                <TintedImage
                  tintColor="darkTint"
                  src={require('galarm-res/img/web/ic_title.svg').default}
                />
                {/* Grommet's TextInput attaches onKeyDown as a document-level listener while
                    focused, which fires only after the event has already bubbled past the
                    enclosing HoverBox's onClick-driven Box (which calls preventDefault() on
                    Space to avoid page scroll). Stopping propagation here, on a plain wrapper
                    between the input and the HoverBox, intercepts the keydown before that. */}
                <Box flex onKeyDown={event => event.stopPropagation()}>
                  <LabelText>{I18n.t('title')}</LabelText>
                  <TextInput
                    flex
                    value={props.name}
                    placeholder={AlarmUtils.getDefaultAlarmName(props.type)}
                    onChange={onNameChange}
                  />
                </Box>
              </HoverBox>
              <HoverBox
                onClick={displayParticipantsPicker}
                background={
                  additionalConfigurationToShow.participantsPicker
                    ? 'lightPrimary'
                    : 'textBackgroundColor'
                }>
                <Box direction="row" gap="medium" align="start">
                  <TintedImage
                    tintColor="darkTint"
                    src={require('galarm-res/img/web/ic_person.svg').default}
                    margin={{ vertical: '15px' }}
                  />
                  <Box>
                    <LabelText>{I18n.t('participants')}</LabelText>
                    {props.backupGroup !== null ||
                    props.backupContacts.length > 0 ? (
                      <ParticipantsList
                        type={props.type}
                        backupGroup={props.backupGroup}
                        backupContacts={props.backupContacts}
                        recipient={props.recipient}
                      />
                    ) : (
                      <Text color="darkTint">
                        {I18n.t('selectParticipants')}
                      </Text>
                    )}
                  </Box>
                </Box>
                <FormNext />
              </HoverBox>
              <HoverBox
                onClick={() => {
                  hideAdditionalConfiguration()
                  handleChangeNotes()
                }}>
                <Box flex>
                  <Box flex direction="row" align="center" gap="medium">
                    <TintedImage
                      tintColor="darkTint"
                      src={require('galarm-res/img/web/ic_notes.svg').default}
                      alignSelf="start"
                    />
                    <Box
                      flex
                      direction="row"
                      align="center"
                      justify="between"
                      gap="medium">
                      <Text color="darkTint">{I18n.t('notes')}</Text>
                      <Box direction="row" gap="medium">
                        <TintedImage
                          src={
                            require('galarm-res/img/web/ic_alarm_text.svg')
                              .default
                          }
                        />
                        <Box onClick={onShowAlarmPhoto}>
                          <TintedImage
                            src={
                              require('galarm-res/img/web/ic_alarm_photo.svg')
                                .default
                            }
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  <Box>
                    {!!props.alarmPhotoUrl && (
                      <Box
                        onClick={displayAlarmPhotoSelector}
                        margin={{
                          vertical: 'small',
                          left: 'large'
                        }}
                        width="80%">
                        <Image src={props.alarmPhotoUrl} />
                      </Box>
                    )}
                    {!!props.notes && (
                      <Box flex margin={{ right: 'small', left: 'large' }}>
                        <RichTextEditor
                          notes={props.notes}
                          onNotesChange={props.onNotesChange}
                          readOnly="true"
                          linksDisabled="true"
                        />
                      </Box>
                    )}
                  </Box>
                </Box>
              </HoverBox>
            </Box>
          </Box>
          {additionalConfigurationToShow.participantsPicker && (
            <Box border="right">
              <ConfigureAlarmParticipants
                type={props.type}
                backupGroup={props.backupGroup}
                backupContacts={props.backupContacts}
                onSelectParticipant={onSelectParticipant}
                onUnselectParticipant={onUnselectParticipant}
                updateParticipantsOrder={updateParticipantsOrder}
                updateMembersConfig={updateMembersConfig}
              />
            </Box>
          )}
          {showNotes && (
            <Layer
              background="lightTint"
              onEsc={() => setShowNotes(false)}
              onClickOutside={() => setShowNotes(false)}>
              <Box margin="xsmall" direction="column">
                <Box pad={{ horizontal: 'small' }}>
                  <RichTextEditor
                    notes={props.notes}
                    onNotesChange={props.onNotesChange}
                    onDone={() => setShowNotes(false)}
                    onCancel={() => setShowNotes(false)}
                  />
                </Box>
              </Box>
            </Layer>
          )}
          <div ref={scrollToRightRef} />
        </Box>
      </Box>
      {showAlarmPhotoSelector && (
        <Layer
          background="lightTint"
          onEsc={hideAlarmPhotoSelector}
          onClickOutside={hideAlarmPhotoSelector}>
          <SelectImage
            onClose={hideAlarmPhotoSelector}
            onUpload={onSelectUploadImage}
            onRemove={onRemoveImage}
            options={options}
            colorScheme={colorScheme}
          />
        </Layer>
      )}
      {
        <input
          type="file"
          id="file"
          accept="image/jpeg"
          ref={inputFile}
          style={{ display: 'none' }}
          onChange={onSelectFile}
        />
      }
    </Box>
  )
}

export default EditInstantAlarm
